"use client";

import { useEffect, useRef } from "react";
import type { Map as LeafletMap, Marker as LeafletMarker } from "leaflet";
import "leaflet/dist/leaflet.css";

interface MapProps {
  latitude: number;
  longitude: number;
  height?: string | number;
  width?: string | number;
  readonly?: boolean;
  onLocationSelect?: (latitude: number, longitude: number) => void;
}

const MapView = ({
  latitude,
  longitude,
  height = 320,
  width = "100%",
  readonly = true,
  onLocationSelect,
}: MapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);
  const onLocationSelectRef = useRef(onLocationSelect);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    const setup = async () => {
      const leafletModule = await import("leaflet");
      const L = leafletModule.default;

      if (cancelled || !containerRef.current) return;

      // Strict Mode can leave a stale Leaflet id on the same DOM node
      const leafletContainer = container as HTMLDivElement & {
        _leaflet_id?: number;
      };
      if (leafletContainer._leaflet_id) {
        leafletContainer.innerHTML = "";
        delete leafletContainer._leaflet_id;
      }

      const map = L.map(container, {
        center: [latitude, longitude],
        zoom: 16,
        scrollWheelZoom: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const icon = L.icon({
        iconUrl: "/assets/addressonmap.svg",
        iconSize: [20, 35],
        iconAnchor: [10, 35],
      });

      const marker = L.marker([latitude, longitude], { icon }).addTo(map);

      if (!readonly) {
        map.on("click", (event) => {
          const { lat, lng } = event.latlng;
          marker.setLatLng([lat, lng]);
          onLocationSelectRef.current?.(lat, lng);
        });
      }

      mapRef.current = map;
      markerRef.current = marker;

      requestAnimationFrame(() => {
        map.invalidateSize();
      });
    };

    void setup();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;

      const leafletContainer = container as HTMLDivElement & {
        _leaflet_id?: number;
      };
      leafletContainer.innerHTML = "";
      delete leafletContainer._leaflet_id;
    };
    // Init once per mount; position updates are handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readonly]);

  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;

    map.setView([latitude, longitude], 16);
    marker.setLatLng([latitude, longitude]);
  }, [latitude, longitude]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
        borderRadius: 15,
        overflow: "hidden",
      }}
    />
  );
};

export default MapView;
